// @ts-check

import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  ChoiceList,
  EmptySearchResult,
  IndexFilters,
  IndexFiltersMode,
  IndexTable,
  InlineStack,
  Popover,
  Spinner,
  Text,
  Tooltip,
  useBreakpoints,
  useSetIndexFiltersMode,
} from "@shopify/polaris";
import { LayoutColumns3Icon } from "@shopify/polaris-icons";

import { AdsTableRow } from "./AdsTableRow";

import { AD_CAMPAIGNS_PAGE_SIZE } from "utils/ad-campaigns";

import { getStartingTableColumns, TABLE_COLUMNS } from "./utils";

const RESOURCE_NAME = { singular: "ad", plural: "ads" };

export const AdsTable = ({ ads, loading, page, setPage }) => {
  const { mode, setMode } = useSetIndexFiltersMode(IndexFiltersMode.Default);

  // TODO add pagination
  const adCampaignsTotalCount = ads.length;
  const [tabIndex, setTabIndex] = useState(0);
  const [editColumnsPopoverActive, setEditColumnsPopoverActive] = useState(false);
  const [columns, setColumns] = useState(getStartingTableColumns());

  const tabs = [
    {
      content: "All",
      index: 0,
      onAction: () => setTabIndex(0),
      id: "all",
    },
  ];

  const handleClearAll = () => {};

  const handleCancel = () => {
    setMode(IndexFiltersMode.Default);
  };

  const handleColumnsChange = (value) => {
    if (!value.length) {
      return;
    }

    return setColumns(value);
  };

  const togglePopoverActive = () => {
    setEditColumnsPopoverActive(active => !active);
  };

  const sortedValues = TABLE_COLUMNS.map((col) => col.value);

  const headings = TABLE_COLUMNS
    .slice()
    .filter(({ value }) => columns.includes(value))
    .sort((a, b) => sortedValues.indexOf(a.value) - sortedValues.indexOf(b.value))
    .map((column) => {
      return {
        id: column.label,
        alignment: column.numeric || column.value === "actions" ? "end" : "start",
        title: column.label,
      };
    });

  const emptyStateMarkup = (
    <EmptySearchResult
      title={`No ${RESOURCE_NAME.plural} yet`}
      description="Try reloading the ad campaigns or editing the search/filters"
      withIllustration
    />
  );

  const editColumnsActivator = (
    <Tooltip content="Edit columns" dismissOnMouseOut>
      <Button
        size="slim"
        icon={LayoutColumns3Icon}
        onClick={togglePopoverActive}
      />
    </Tooltip>
  );

  const totalPages = ~~(adCampaignsTotalCount / AD_CAMPAIGNS_PAGE_SIZE) + 1;

  const paginationLabel = (
    <InlineStack gap="300" align="center">
      <Text fontWeight="semibold" as="p">Page {page} of {totalPages}</Text>
      <Text as="p">Showing {ads.length} of {new Intl.NumberFormat("en-US").format(adCampaignsTotalCount)} {RESOURCE_NAME.plural}</Text>
      {loading && <Spinner size="small" />}
    </InlineStack>
  );

  let columnFilter;
  if (useBreakpoints().smUp) {
    columnFilter = (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          paddingRight: "var(--p-space-200)",
          borderBottom: "var(--p-border-width-025) solid var(--p-color-border)",
        }}
      >
        <Popover
          active={editColumnsPopoverActive}
          activator={editColumnsActivator}
          autofocusTarget="first-node"
          onClose={togglePopoverActive}
        >
          <Box padding="400">
            <ChoiceList
              allowMultiple
              title="Columns"
              choices={TABLE_COLUMNS.filter(({ removable }) => removable)}
              selected={columns}
              onChange={handleColumnsChange}
            />
          </Box>
        </Popover>
      </div>
    );
  }

  return (
    <Card padding="0">
      {/* <div style={{ display: "flex", flexDirection: "row" }}>
        <IndexFilters
          queryValue={""}
          queryPlaceholder="Search by order number"
          onQueryChange={() => {}}
          onQueryClear={() => {}}
          cancelAction={{
            onAction: handleCancel,
            disabled: false,
            loading: false,
          }}
          tabs={tabs}
          selected={tabIndex}
          loading={loading}
          onClearAll={handleClearAll}
          mode={mode}
          setMode={setMode}
          filters={[]}
          canCreateNewView={false}
        />
        {columnFilter}
      </div> */}

      <Box padding="300">
        <Text as="h2" variant="headingMd">Ads</Text>
      </Box>

      <IndexTable
        condensed={useBreakpoints().smDown}
        resourceName={RESOURCE_NAME}
        itemCount={ads.length}
        selectable={false}
        emptyState={emptyStateMarkup}
        headings={headings}
        lastColumnSticky
        pagination={{
          label: paginationLabel,
          hasPrevious: page > 1,
          hasNext: AD_CAMPAIGNS_PAGE_SIZE * page < adCampaignsTotalCount,
          onNext: () => {
            setPage(page => Math.min(totalPages, page + 1));
          },
          onPrevious: () => {
            setPage(page => Math.max(0, page - 1));
          },
        }}
      >
        {ads.map((row, index) => (
          <AdsTableRow
            key={index}
            index={index}
            data={row}
            columns={columns}
          />
        ))}
      </IndexTable>
    </Card>
  );
};
